<template>
  <div class="meetings-page" :style="{ backgroundImage: `url(${designUrl}${isMobile ? 'MeetingsPage_phone.png' : 'MeetingsPage.jpeg'})` }">
    <div class="sidebar-wrapper">
      <button class="hamburger" @click="sidebarOpen = !sidebarOpen">☰</button>
      <div class="sidebar" :class="{ open: sidebarOpen }">
        <div class="sidebar-header">
          <button class="close-btn" @click="sidebarOpen = false">✖</button>
          <p>{{ user.fullname }}</p>
          <p>{{ user.phone_number }}</p>
        </div>
        <ul class="sidebar-menu">

          <template v-if="user && user.user_type === 'Pet Owner'">
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li class="has-submenu" @click="petsOpen = !petsOpen">
              My Pets
              <span class="arrow" :class="{ 'arrow-open': petsOpen }">›</span>
            </li>
            <ul class="sub-menu" :class="{ 'sub-open': petsOpen }">
              <li @click="goToPage('YourPetsPage')">View My Pets</li>
              <li @click="goToPage('AddPetPage')">Add Pet</li>
              <li @click="goToPage('MeetingsPage')">Booking Requests</li>
            </ul>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

          <template v-else>
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li @click="goToPage('CalendarPage')">My Calendar</li>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

        </ul>
      </div>
    </div>

    <div class="page-header">
      <h1 class="page-title">Petting Requests</h1>
    </div>

    <div class="calendar-button-wrapper">
      <button @click="goToCalendar" class="calendar-button">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
      </button>
    </div>

    <div class="page-wrapper">

      <div class="controls-row">
        <div class="tab-group">
          <button class="tab-btn" :class="{ 'tab-active': activeTab === 'all' }" @click="activeTab = 'all'">All</button>
          <button class="tab-btn" :class="{ 'tab-active': activeTab === 'pending' }" @click="activeTab = 'pending'">Pending</button>
          <button class="tab-btn" :class="{ 'tab-active': activeTab === 'accepted' }" @click="activeTab = 'accepted'">Accepted</button>
          <button class="tab-btn" :class="{ 'tab-active': activeTab === 'denied' }" @click="activeTab = 'denied'">Denied</button>
        </div>
      </div>

      <div v-if="filteredRequests.length === 0" class="empty-state">
        <div class="empty-icon">📅</div>
        <p class="empty-text">No requests here</p>
        <p class="empty-sub">{{
          activeTab === 'all' ? 'Incoming meeting requests will appear here' :
          activeTab === 'pending' ? 'No pending requests' :
          activeTab === 'accepted' ? 'No accepted requests yet' :
          'No denied requests yet'
        }}</p>
      </div>

      <div v-else class="requests-list">
        <div v-for="req in filteredRequests" :key="req.id" class="booking-card">
          <div class="card-top">
            <div class="booking-date-block">
              <span class="booking-day">{{ formatDay(req.date) }}</span>
              <span class="booking-month">{{ formatMonth(req.date) }}</span>
            </div>
            <div class="booking-middle">
              <p class="booking-pet-name">{{ req.pet_name }}</p>
              <p class="booking-time">{{ formatTime(req.time) }}</p>
            </div>
            <span class="status-badge" :class="statusClass(req.confirmation)">
              {{ getStatusLabel(req.confirmation) }}
            </span>
          </div>

          <div class="card-actions">
            <button
              class="action-btn accept-btn"
              @click="confirm(req.id, true)"
              :disabled="req.confirmation === 1"
              :class="{ 'btn-active': req.confirmation === 1 }"
            >✓ Accept</button>
            <button
              class="action-btn deny-btn"
              @click="confirm(req.id, false)"
              :disabled="req.confirmation === 0"
              :class="{ 'btn-active': req.confirmation === 0 }"
            >✕ Deny</button>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import axios from 'axios';
const backendUrl = process.env.VUE_APP_API_URL;

export default {
  name: 'MeetingsPage',
  props: {
    user: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      designUrl: '/background_designs/',
      isMobile: window.innerWidth <= 480,
      requests: [],
      sidebarOpen: false,
      activeTab: 'all',
      petsOpen: false,
    };
  },
  computed: {
    filteredRequests() {
      if (this.activeTab === 'all') return this.requests;
      if (this.activeTab === 'pending') return this.requests.filter(r => r.confirmation === null);
      if (this.activeTab === 'accepted') return this.requests.filter(r => r.confirmation === 1);
      if (this.activeTab === 'denied') return this.requests.filter(r => r.confirmation === 0);
      return this.requests;
    }
  },
  methods: {
    goToCalendar() {
      this.$emit('change-page', 'CalendarPage');
    },
    goToPage(pageName) {
      this.sidebarOpen = false;
      this.$emit('change-page', pageName);
    },
    async load() {
      try {
        const { data } = await axios.get(`${backendUrl}/api/meetings/requests`, { withCredentials: true });
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        this.requests = data
          .filter(req => {
            const reqDate = new Date(req.date);
            reqDate.setHours(0, 0, 0, 0);
            return reqDate >= today;
          })
          .sort((a, b) => new Date(b.date) - new Date(a.date));
      } catch (e) {
        console.error('Load requests failed', e);
      }
    },
    getStatusLabel(status) {
      if (status === 1) return 'Accepted';
      if (status === 0) return 'Denied';
      return 'Pending';
    },
    formatTime(timeStr) {
      const [h, m] = timeStr.split(':');
      let hour = parseInt(h, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      hour = hour % 12 || 12;
      return `${hour}:${m} ${ampm}`;
    },
    async confirm(id, yes) {
      try {
        await axios.patch(
          `${backendUrl}/api/meetings/${id}/confirmation`,
          { confirmation: yes },
          { withCredentials: true }
        );
        this.load();
      } catch (e) {
        console.error('Confirm failed', e);
      }
    },
    formatDay(dateStr) {
      return String(new Date(dateStr).getDate()).padStart(2, '0');
    },
    formatMonth(dateStr) {
      return new Date(dateStr).toLocaleString('default', { month: 'short' }).toUpperCase();
    },
    statusClass(status) {
      if (status === 1) return 'status-accepted';
      if (status === 0) return 'status-denied';
      return 'status-pending';
    },
    handleResize() {
      this.isMobile = window.innerWidth <= 480;
    },
  },
  mounted() {
    this.load();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style scoped>
* { margin: 0; padding: 0; box-sizing: border-box; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }

.meetings-page {
  min-height: 100vh;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #f5ece0;
}

.page-header {
  max-width: 1200px;
  margin: 0 auto;
  padding: 28px 20px 0 24px;
}

.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.4px;
  margin: 0;
  margin-top: 8px; margin-left: 7px;
}

.page-sub {
  font-size: 13px;
  color: #aaa;
  font-weight: 500;
  margin-top: 2px;
}

.page-wrapper {
  max-width: 1000px;
  margin: 0 auto;
  padding: 16px 20px 40px;
}

.controls-row { display: flex; align-items: center; margin-bottom: 24px; }
.tab-group { display: flex; background: #fff; border: 1px solid #f0e0ff; border-radius: 999px; padding: 4px; gap: 4px; }
.tab-btn { padding: 8px 16px; border-radius: 999px; border: none; background: none; font-size: 13px; font-weight: 700; color: #aaa; cursor: pointer; transition: all 0.18s ease; }
.tab-btn:hover { color: #4a1070; }
.tab-active { background: #eac7ff; color: #4a1070; }

.requests-list { display: flex; flex-direction: column; gap: 14px; }

.booking-card { background: #fff; border-radius: 20px; border: 1px solid #f0e0ff; padding: 18px 20px; box-shadow: 0 4px 16px rgba(186,113,255,0.07); transition: transform 0.2s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.2s; }
.booking-card:hover { transform: translateY(-3px); box-shadow: 0 10px 28px rgba(186,113,255,0.13); }

.card-top { display: flex; align-items: center; gap: 16px; margin-bottom: 16px; overflow-y: auto; max-height: calc(100vh - 250px); }

.booking-date-block { flex-shrink: 0; width: 52px; height: 58px; background: linear-gradient(135deg, #eac7ff, #dda6fd); border-radius: 14px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1px; }
.booking-day { font-size: 20px; font-weight: 800; color: #4a1070; line-height: 1; }
.booking-month { font-size: 10px; font-weight: 700; color: #7a30a8; letter-spacing: 0.06em; }

.booking-middle { flex: 1; min-width: 0; }
.booking-pet-name { font-size: 16px; font-weight: 800; color: #1a1a1a; margin: 0 0 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.booking-time { font-size: 13px; color: #999; font-weight: 600; margin: 0; }

.status-badge { flex-shrink: 0; font-size: 12px; font-weight: 700; padding: 5px 13px; border-radius: 999px; letter-spacing: 0.02em; }
.status-accepted { background: #d4f5e2; color: #1a6b3c; }
.status-denied { background: #fde8e8; color: #991b1b; }
.status-pending { background: #fffbe8; color: #92610a; border: 1px solid #f6d860; }

.card-actions { display: flex; gap: 10px; border-top: 1px solid #fdf5ff; padding-top: 14px; }
.action-btn { flex: 1; padding: 10px 0; border-radius: 12px; font-size: 13px; font-weight: 700; border: none; cursor: pointer; transition: background 0.18s, transform 0.1s; letter-spacing: 0.02em; }
.action-btn:active { transform: scale(0.97); }
.action-btn:disabled { opacity: 0.45; cursor: not-allowed; transform: none; }

.accept-btn { background: #d4f5e2; color: #53c181; }
.accept-btn:not(:disabled):hover { background: #a8edca; }
.accept-btn.btn-active { background: #53c181; color: #fff; opacity: 1; }

.deny-btn { background: #fde8e8; color: #991b1b; }
.deny-btn:not(:disabled):hover { background: #fbc8c8; }
.deny-btn.btn-active { background: #991b1b; color: #fff; opacity: 1; }

.empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px 20px; gap: 10px; }
.empty-icon { font-size: 48px; opacity: 0.5; }
.empty-text { font-size: 17px; font-weight: 700; color: #333; margin: 0; }
.empty-sub { font-size: 13px; color: #bbb; margin: 0; }

.calendar-button-wrapper {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 2000;
}

.calendar-button {
  width: 55px;
  height: 55px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, #fffbe8, #fff8d3);
  color: #1e3a8a;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  transition: transform 0.2s, background 0.2s, box-shadow 0.2s;
}

.calendar-button:hover {
  transform: scale(1.1);
  background: linear-gradient(135deg, #ffe876, #fffbe8);
  box-shadow: 0 6px 16px rgba(30,58,138,0.25);
}

@media (max-width: 480px) {
  .page-header { padding: 20px 16px 0 46px; }
  .page-sub { font-size: 11px; }
  .page-wrapper { padding: 12px 14px 30px; }

  .requests-list { gap: 10px; }

  .booking-card {
    padding: 10px;
    border-radius: 16px;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
  }

  .card-top {
    flex: 1;
    gap: 10px;
    margin-bottom: 0;
    align-items: center;
  }

  .booking-date-block {
    width: 44px;
    height: 50px;
    border-radius: 10px;
    flex-shrink: 0;
  }

  .booking-day { font-size: 16px; }
  .booking-month { font-size: 9px; }
  .booking-pet-name { font-size: 13px; }
  .booking-time { font-size: 11px; }

  .status-badge {
    font-size: 10px;
    padding: 3px 8px;
  }

  .card-actions {
    border-top: none;
    padding-top: 0;
    flex-direction: column;
    gap: 5px;
    flex-shrink: 0;
    width: 72px;
  }

  .action-btn {
    font-size: 11px;
    padding: 7px 0;
    border-radius: 10px;
  }

  .tab-btn { padding: 6px 10px; font-size: 12px; }
}
</style>